import { Component, OnInit, Input } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { FormBuilder, FormGroup, FormArray } from '@angular/forms';
import { ProviderDataValidators as Validators } from '@app/modules/data-valiidator';
import { SpinnerService } from '@app/@shared/spinner/spinner.service';
import { UserService } from '@app/services/user.service';
import { QuestionType } from '@app/interface/survey';
import { ModalService } from '@app/@core/modal/modal.service';
import { take } from 'rxjs/operators';
import Utils from 'src/utilities/utils';
import { SurveyQuestionTypeEnumes } from '@shared/enums/surveyQuestionType';
import { AngularFirestore } from '@angular/fire/firestore';

@Component({
  selector: 'app-add-question',
  templateUrl: './surveyDetailQuestionForm.component.html',
  styleUrls: ['./surveyDetailQuestionForm.component.scss'],
})
export class SurveyDetailQuestionFormComponent implements OnInit {
  surveyQuestionForm: FormGroup;
  readonly dateYearRange = '1940:2030';
  submitted = false;
  success: boolean;
  isLoading = false;
  selectedQuestionType: string;
  rangeDates: Date[];
  minDate = new Date();
  maxDate = new Date();
  qtypeFields: QuestionType[] = [
    { value: 'number', viewValue: SurveyQuestionTypeEnumes.NUMBER },
    { value: 'string', viewValue: SurveyQuestionTypeEnumes.STRING },
    { value: 'scale', viewValue: SurveyQuestionTypeEnumes.SCALE },
    { value: 'time', viewValue: SurveyQuestionTypeEnumes.TIME },
    { value: 'options', viewValue: SurveyQuestionTypeEnumes.OPTIONS },
    { value: 'number-range', viewValue: SurveyQuestionTypeEnumes.NUMBER_RANGE },
    { value: 'date-range', viewValue: SurveyQuestionTypeEnumes.DATE_RANGE },
    { value: 'time-range', viewValue: SurveyQuestionTypeEnumes.TIME_RANGE },
    { value: 'multiple-option', viewValue: SurveyQuestionTypeEnumes.MULTIPLE_OPTIONS },
  ];

  @Input() title_tag: string;
  @Input() formType: string;
  @Input() form_data: {};
  @Input() data_table: [];
  @Input() uuid_data: string;
  @Input() show_answers: boolean = false;

  constructor(
    public activeModal: NgbActiveModal,
    private formBuilder: FormBuilder,
    private spinnerService: SpinnerService,
    public userService: UserService,
    private modalService: ModalService,
    private afs: AngularFirestore
  ) {}

  ngOnInit() {
    this.getAddsurveyQuestionForm();
    this.isFormTypeEdit(this.formType) && this.getQuestionUUID(this.form_data['uuid']);
  }

  public isFormTypeEdit(formType: string) {
    switch (formType) {
      case 'Edit':
        return true;
        break;
      case 'Add':
        return false;
        break;
      default:
        return false;
        break;
    }
  }

  public get surveyQuestionTypeEnumes(): any {
    return SurveyQuestionTypeEnumes;
  }

  public selectQuestionType(e: any): void {
    this.selectedQuestionType = e.value['viewValue'];
    if (
      this.selectedQuestionType === SurveyQuestionTypeEnumes.DATE_RANGE ||
      this.selectedQuestionType === SurveyQuestionTypeEnumes.TIME_RANGE
    ) {
      this.surveyQuestionForm.setValue({
        min: new Date(),
        max: new Date(),
        step: null,
      });
    } else {
      this.surveyQuestionForm.patchValue({
        min: null,
        max: null,
        step: null,
      });
    }
    this.surveyQuestionForm.setControl(
      'options',
      this.isFormTypeEdit(this.formType) && this.form_data['options'] !== undefined
        ? this.formBuilder.array(this.form_data['options'].map((r: any) => this.newOption(r)))
        : this.formBuilder.array([])
    );
  }

  public getQuestionUUID(questionUUID: string) {
    this.userService.getSurveyQuestionUUID(questionUUID);
  }

  // convenience getter for easy access to form fields
  get f() {
    return this.surveyQuestionForm.controls;
  }

  changeDependenciesBeforeSumbit(form: any) {
    return form.map((o: any) => {
      o['qUUID'] = o['qUUID']['uuid'];
      o['answer1'] = o['answer1'].map((arr: any) => {
        return arr.ans;
      });
      return o;
    });
  }

  checkQuestionFormSubmissionValidation(formData: any): any {
    if (
      this.selectedQuestionType === SurveyQuestionTypeEnumes.OPTIONS ||
      this.selectedQuestionType === SurveyQuestionTypeEnumes.MULTIPLE_OPTIONS
    ) {
      delete formData['min'];
      delete formData['max'];
      delete formData['step'];
    } else if (
      this.selectedQuestionType === SurveyQuestionTypeEnumes.STRING ||
      this.selectedQuestionType === SurveyQuestionTypeEnumes.NUMBER ||
      this.selectedQuestionType === SurveyQuestionTypeEnumes.NUMBER_RANGE ||
      this.selectedQuestionType === SurveyQuestionTypeEnumes.DATE_RANGE ||
      this.selectedQuestionType === SurveyQuestionTypeEnumes.TIME_RANGE
    ) {
      delete formData['options'];
      delete formData['step'];
    } else if (this.selectedQuestionType === SurveyQuestionTypeEnumes.TIME) {
      delete formData['options'];
      delete formData['step'];
      delete formData['min'];
      delete formData['max'];
    } else if (this.selectedQuestionType === SurveyQuestionTypeEnumes.SCALE) {
      delete formData['options'];
    }
  }

  public convertDateToYear(val: any) {
    return Utils.convertDate(val, 'year');
  }

  save() {
    this.submitted = true;
    const surveyQuestionData = this.surveyQuestionForm.value;
    this.checkQuestionFormSubmissionValidation(surveyQuestionData);
    if (this.surveyQuestionForm.valid) {
      surveyQuestionData['qtype'] = surveyQuestionData['qtype']['value'];
      surveyQuestionData['dependencies'] = this.changeDependenciesBeforeSumbit(surveyQuestionData['dependencies']);
      this.isLoading = true;
      this.spinnerService.show();
      this.submitAddEditQuestion(surveyQuestionData);
    } else {
      return;
    }
  }

  submitAddEditQuestion(questionData: any): void {
    this.userService.addSurveyQuestion(questionData, this.uuid_data).then(() => {
      this.isLoading = false;
      this.submitted = false;
      this.activeModal.close(true);
      this.spinnerService.hide();
    });
  }

  get optionsData(): FormArray {
    return this.surveyQuestionForm.get('options') as FormArray;
  }

  get dependenciesData(): FormArray {
    return this.surveyQuestionForm.get('dependencies') as FormArray;
  }

  answersData(form: any): FormArray {
    return form.controls.answer1.controls;
  }

  newAnswers(existingData?: any): FormGroup {
    return this.formBuilder.group({
      ans: [existingData ? existingData : '', [Validators.required()]],
    });
  }

  newOption(existingData?: any): FormGroup {
    return this.formBuilder.group({
      val: [existingData ? existingData.val : '', [Validators.required()]],
      text: [existingData ? existingData.text : '', [Validators.required()]],
    });
  }

  setQUUIDDValue(status: boolean): any {
    let index = this.data_table.map((item: any) => item.uuid).indexOf(status);
    return this.data_table[index];
  }

  newDependencies(existingData?: any): FormGroup {
    return this.formBuilder.group({
      qUUID: [existingData ? this.setQUUIDDValue(existingData.qUUID) : null, [Validators.required()]],
      answer1: existingData
        ? this.formBuilder.array(existingData['answer1'].map((r: any) => this.newAnswers(r)))
        : this.formBuilder.array([this.newAnswers()], [Validators.required()]),
    });
  }

  addNewOption() {
    if (this.optionsData.valid) {
      this.optionsData.push(this.newOption());
    } else {
      return;
    }
  }

  removeOption(i: number) {
    this.modalService
      .confirm('Are you sure that you want to remove this ?')
      .pipe(take(1))
      .subscribe((confirmed: any) => {
        if (confirmed) {
          this.optionsData.removeAt(i);
        }
      });
  }

  addNewDependency() {
    if (this.dependenciesData.valid) {
      this.dependenciesData.push(this.newDependencies());
    } else {
      return;
    }
  }

  removeDependency(i: number) {
    this.modalService
      .confirm('Are you sure that you want to remove this ?')
      .pipe(take(1))
      .subscribe((confirmed: any) => {
        if (confirmed) {
          this.dependenciesData.removeAt(i);
        }
      });
  }

  addNewAnswer(index: any) {
    if (<FormArray>this.surveyQuestionForm.get('dependencies')['controls'][index].get('answer1').valid) {
      const control = <FormArray>this.surveyQuestionForm.get('dependencies')['controls'][index].get('answer1');
      control.push(this.newAnswers());
    } else {
      return;
    }
  }

  removeAnswer(i: number, j: number) {
    this.modalService
      .confirm('Are you sure that you want to remove this ?')
      .pipe(take(1))
      .subscribe((confirmed: any) => {
        if (confirmed) {
          const control = <FormArray>this.surveyQuestionForm.get('dependencies')['controls'][i].get('answer1');
          control.removeAt(j);
        }
      });
  }

  setQTypeValue(status: boolean): any {
    let index = this.qtypeFields.map((item: any) => item.value).indexOf(status);
    this.selectedQuestionType = this.qtypeFields[index]['viewValue'];
    return this.qtypeFields[index];
  }

  checkValidator(type: string, validatorType: string): any {
    if (type === SurveyQuestionTypeEnumes.OPTIONS || type === SurveyQuestionTypeEnumes.MULTIPLE_OPTIONS) {
      return false;
    } else if (
      (type === SurveyQuestionTypeEnumes.STRING ||
        type === SurveyQuestionTypeEnumes.NUMBER ||
        type === SurveyQuestionTypeEnumes.NUMBER_RANGE ||
        type === SurveyQuestionTypeEnumes.DATE_RANGE ||
        type === SurveyQuestionTypeEnumes.TIME_RANGE) &&
      (validatorType === 'min' || validatorType === 'max')
    ) {
      return true;
    } else if (type === SurveyQuestionTypeEnumes.SCALE && validatorType === 'step') {
      return true;
    } else {
      return false;
    }
  }

  setBasedQTypeValue(val: any) {
    if (
      this.selectedQuestionType === SurveyQuestionTypeEnumes.DATE_RANGE ||
      this.selectedQuestionType === SurveyQuestionTypeEnumes.TIME_RANGE
    ) {
      var seconds = val?.seconds ? new Date(val.seconds * 1000) : null;
      return seconds;
    } else {
      return val;
    }
  }

  getAddsurveyQuestionForm() {
    this.isFormTypeEdit(this.formType) && this.setQTypeValue(this.form_data['qtype']);
    this.surveyQuestionForm = this.formBuilder.group({
      uuid: [
        this.isFormTypeEdit(this.formType) ? this.form_data['uuid'] : this.afs.createId(),
        [Validators.required()],
      ],
      qtext: [this.isFormTypeEdit(this.formType) ? this.form_data['qtext'] : '', [Validators.required()]],
      qtype: [
        this.isFormTypeEdit(this.formType) ? this.setQTypeValue(this.form_data['qtype']) : null,
        [Validators.required()],
      ],
      sequence: [this.isFormTypeEdit(this.formType) ? this.form_data['sequence'] : '', [Validators.required()]],
      unit: [this.isFormTypeEdit(this.formType) ? this.form_data['unit'] : '', []],
      min: [this.isFormTypeEdit(this.formType) ? this.setBasedQTypeValue(this.form_data['min']) : null, []],
      max: [this.isFormTypeEdit(this.formType) ? this.setBasedQTypeValue(this.form_data['max']) : null, []],
      step: [this.isFormTypeEdit(this.formType) ? this.form_data['step'] : null, []],
      options:
        this.isFormTypeEdit(this.formType) && this.form_data['options'] !== undefined
          ? this.formBuilder.array(this.form_data['options'].map((r: any) => this.newOption(r)))
          : this.formBuilder.array([]),
      dependencies:
        this.isFormTypeEdit(this.formType) && this.form_data['dependencies'] !== undefined
          ? this.formBuilder.array(this.form_data['dependencies'].map((r: any) => this.newDependencies(r)))
          : this.formBuilder.array([]),
    });
  }
}
